import type { IPaginationMetadata } from "@/shared/interfaces/api-response.interface";
import type { IBoundarySchoolLink } from "@/shared/types/boundary-school-link.types";

import { type SchoolTypeEnum } from "../reports/reports.interface";

export type { IBoundarySchoolLink };

export enum CustomLayerTypeFilter {
  ALL = "all",
  IMPORTED = "imported",
  CUSTOM = "custom",
}

export enum CustomLayerOriginType {
  IMPORTED = "IMPORTED",
  CUSTOM = "CUSTOM",
}

export enum CustomLayerStatus {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
  APPLIED = "APPLIED",
  MERGED = "MERGED",
}

export enum CustomLayerTargetType {
  SCHOOL_BOUNDARY = "SCHOOL_BOUNDARY",
  DISTRICT_BOUNDARY = "DISTRICT_BOUNDARY",
}

export interface ICustomLayerCreator {
  id: string;
  fullName: string;
}

export interface ICustomLayer {
  id: string;
  name: string;
  districtId: string;
  status: CustomLayerStatus;
  jobId: string | null;
  scheduledMergeDate: string | null;
  archivedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdById: string | null;
  creator: ICustomLayerCreator | null;
  type: SchoolTypeEnum | null;
  layerType: CustomLayerOriginType;
  targetType: CustomLayerTargetType;
}

export interface ICustomLayerBoundary {
  id: string;
  schoolBoundaryId: string | null;
  geometryGeoJson: string;
  schoolTypes: SchoolTypeEnum[];
  schools: IBoundarySchoolLink[];
}

export interface ICustomLayerBoundaryDetailsGrades {
  lowestGradeLabel: string | null;
  highestGradeLabel: string | null;
}

export interface ICustomLayerBoundaryDetails extends ICustomLayerBoundary {
  grades?: ICustomLayerBoundaryDetailsGrades | null;
  address?: string | null;
  color?: string | null;
}

export interface ICustomLayerListResponse {
  items: ICustomLayer[];
  pagination: IPaginationMetadata;
}

export interface ICustomLayerQaDumpAuditLog {
  id: string;
  action: string;
  area: string;
  entityType: string;
  entityId: string;
  districtId: string | null;
  actorUserId: string;
  actorEmail: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  occurredAt: string;
}

export interface ICustomLayerQaDumpResponse {
  layers: ICustomLayer[];
  auditLogs: ICustomLayerQaDumpAuditLog[];
}

export enum CustomLayerRecoverAction {
  UNARCHIVE = "unarchive",
  MAKE_DRAFT = "make_draft",
}

export interface IRecoverableCustomLayer extends ICustomLayer {
  archivedBy: ICustomLayerCreator | null;
  recoverAction: CustomLayerRecoverAction;
}

export interface IRecoverableCustomLayerListResponse {
  items: IRecoverableCustomLayer[];
}

export interface ICustomLayerBoundaryListResponse {
  items: ICustomLayerBoundary[];
}

export interface ICustomLayerBoundaryOverviewItem {
  id: string;
  geometryGeoJson: string;
  color: string | null;
  isConnected: boolean;
  schools: IBoundarySchoolLink[];
}

export interface ICustomLayerBoundaryOverviewStats {
  layerId: string;
  connectedCount: number;
  notConnectedCount: number;
  totalCount: number;
}

export interface ICustomLayerBoundariesOverviewResponse {
  items: ICustomLayerBoundaryOverviewItem[];
  layerStats: ICustomLayerBoundaryOverviewStats;
}

export interface IGetCustomLayerBoundariesListParams {
  districtId: string;
  layerId: string;
}

export interface IGetCustomLayersParams {
  districtId: string;
  page?: number;
  limit?: number;
  status?: CustomLayerStatus;
  includeArchived?: boolean;
  type?: CustomLayerTypeFilter;
  targetType?: CustomLayerTargetType;
  schoolLevelType?: SchoolTypeEnum;
}

export interface IDuplicateLayerFromSchoolBoundariesRequest {
  name: string;
  schoolLevelType: string;
}

export interface IDuplicateLayerFromDistrictBoundariesRequest {
  name: string;
}

export interface IGetSuggestedCustomLayerNamesParams {
  districtId: string;
  schoolLevelType?: string;
  name?: string;
  count?: number;
}

export interface ISuggestedCustomLayerNamesResponse {
  suggestedNames: string[];
}

export interface IDuplicateCustomLayerRequest {
  name: string;
}

export interface IUpdateCustomLayerRequest {
  name?: string;
  jobId?: string | null;
}

export interface ISetCustomLayerScheduledMergeDateRequest {
  scheduledMergeDate: string | null;
}

export interface ICreateCustomLayerBoundaryRequest {
  geometryGeoJson: string;
  documentId?: string | null;
  schoolBoundaryId?: string | null;
  /** Omit or send [] to create unassigned. */
  schools?: IBoundarySchoolLink[];
}

export interface IUpsertCustomLayerBoundaryRequest {
  id?: string | null;
  geometry: GeoJSON.Geometry;
  documentId?: string | null;
  schoolBoundaryId?: string | null;
  /** Omit to keep current links, send [] to unassign. */
  schools?: IBoundarySchoolLink[];
  deleted?: boolean;
}

export interface IUpdateCustomLayerBoundaryRequest {
  schoolBoundaryId?: string | null;
  /** Omit to keep current links, send [] to unassign. */
  schools?: IBoundarySchoolLink[];
  geometryGeoJson?: string;
}

export interface IUpsertCustomLayerBoundariesRequest {
  boundaries: IUpsertCustomLayerBoundaryRequest[];
}

export interface IUpdateCustomLayerBoundariesItemRequest {
  boundaryId: string;
  deleted?: boolean;
  schoolBoundaryId?: string | null;
  /** Omit to keep current links, send [] to unassign. */
  schools?: IBoundarySchoolLink[];
  geometryGeoJson?: string;
}

export interface IUpdateCustomLayerBoundariesRequest {
  updates: IUpdateCustomLayerBoundariesItemRequest[];
}

export interface IExportCustomLayerBoundariesZipArg {
  districtId: string;
  layerId: string;
}

export interface IDownloadCustomLayerBoundariesZipSuccessResult {
  success: true;
  filename: string;
}

export type GapFillFailureReason =
  | "noGap"
  | "clickInsideBoundary"
  | "clickOutsideDistrict"
  | "geometryFailed"
  | "areaTooSmall";

export interface IDraftBoundaryGeometryRequest {
  id: string;
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

export interface IGapFillDraftOverrides {
  draftBoundaries: IDraftBoundaryGeometryRequest[];
  deletedBoundaryIds: string[];
}

export interface IResolveGapFillRequest {
  lng: number;
  lat: number;
  draftBoundaries?: IDraftBoundaryGeometryRequest[];
  deletedBoundaryIds?: string[];
}

export interface IResolveGapFillResponse {
  geometryGeoJson: string;
}

export interface IResolveAutoFitRequest {
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  editingBoundaryId: string;
  draftBoundaries?: IDraftBoundaryGeometryRequest[];
  deletedBoundaryIds?: string[];
}

export interface IResolveAutoFitResponse {
  geometryGeoJson: string;
}
